import { useMemo } from 'react'
import type { TripItineraryData, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isExperienceSavedData, isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { computeCartPanelTotal, isExperienceBuyNowBookLaterBookingType } from '../utils/cartPanelUtils'
import { useItineraryItemPricing, type PricingInput } from './useItineraryItemPricing'
import { useExperienceCartItemPricing, type ExperiencePricingInput } from './useExperienceCartItemPricing'

export interface CartButtonSummary {
  bookableItemCount: number
  estimatedTotal: number
}

type ItineraryItem = TripItineraryItem | TripItemWithoutDay

interface ExtractedAccommodationItem {
  productType: 'accommodation'
  tripItemId: number
  cartExcluded: boolean
  pricingInput: PricingInput
}

interface ExtractedExperienceItem {
  productType: 'experience'
  tripItemId: number
  cartExcluded: boolean
  pricingInput: ExperiencePricingInput
}

type ExtractedItem = ExtractedAccommodationItem | ExtractedExperienceItem

function extractBookableItems(itineraryData: TripItineraryData | undefined): Array<ExtractedItem> {
  if (!itineraryData?.stops) return []

  const items: Array<ExtractedItem> = []
  const seenItemIds = new Set<number>()

  const processItem = (item: ItineraryItem) => {
    if (item.isBooked) return
    if (seenItemIds.has(item.id)) return

    const sd = item.savedItemData
    if (item.type === 'ACCOMMODATION' && isHotelSavedData(sd)) {
      seenItemIds.add(item.id)
      items.push({
        productType: 'accommodation',
        tripItemId: item.id,
        cartExcluded: item.cartExcluded ?? false,
        pricingInput: {
          tripItemId: item.id,
          offerId: sd.offerId,
          packageId: sd.packageId,
          checkIn: sd.checkIn,
          checkOut: sd.checkOut,
          occupancy: sd.occupancies?.[0],
          duration: sd.packageDuration,
          roomRateId: sd.roomRateId,
          savedPrice: sd.price,
          savedMemberPrice: sd.memberPrice,
          savedValue: sd.value,
        },
      })
      return
    }

    if (item.type === 'EXPERIENCE' && isExperienceSavedData(sd)) {
      seenItemIds.add(item.id)
      items.push({
        productType: 'experience',
        tripItemId: item.id,
        cartExcluded: item.cartExcluded ?? false,
        pricingInput: {
          tripItemId: item.id,
          experienceId: sd.experienceId,
          date: sd.date,
          time: sd.time,
          pickupPointId: sd.pickupPointId,
          redemptionLocationId: sd.redemptionLocationId,
          isBuyNowBookLater: isExperienceBuyNowBookLaterBookingType(sd.bookingType),
          tickets: (sd.tickets ?? []).map(ticket => ({
            ticketId: ticket.ticketId,
            name: ticket.name,
            count: ticket.count,
            date: ticket.date ?? sd.date,
          })),
          savedPrice: sd.price,
          savedMemberPrice: sd.memberPrice,
          savedValue: sd.value,
        },
      })
    }
  }

  for (const stop of itineraryData.stops) {
    for (const day of stop.days) {
      for (const item of day.items) {
        processItem(item)
      }
    }
    for (const item of stop.itemsWithoutDay ?? []) {
      processItem(item)
    }
  }

  return items
}

export function useCartButtonSummary(
  itineraryData: TripItineraryData | undefined,
): CartButtonSummary {
  const extractedItems = useMemo(
    () => extractBookableItems(itineraryData),
    [itineraryData],
  )

  const pricingInputs = useMemo(
    () => extractedItems
      .filter((item): item is ExtractedAccommodationItem => item.productType === 'accommodation')
      .map(item => item.pricingInput),
    [extractedItems],
  )

  const experiencePricingInputs = useMemo(
    () => extractedItems
      .filter((item): item is ExtractedExperienceItem => item.productType === 'experience')
      .map(item => item.pricingInput),
    [extractedItems],
  )

  const pricing = useItineraryItemPricing(pricingInputs)
  const experiencePricing = useExperienceCartItemPricing(experiencePricingInputs)
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)

  return useMemo(() => {
    const bookableItems = extractedItems.map(item => {
      const resolved = item.productType === 'experience'
        ? experiencePricing.get(item.tripItemId)
        : pricing.get(item.tripItemId)
      return {
        cartExcluded: item.cartExcluded,
        price: resolved?.price,
        memberPrice: resolved?.memberPrice,
      }
    })
    const { totalPrice, totalItemCount } = computeCartPanelTotal(bookableItems, hasLuxPlusBenefits)
    return {
      bookableItemCount: totalItemCount,
      estimatedTotal: totalPrice,
    }
  }, [extractedItems, pricing, experiencePricing, hasLuxPlusBenefits])
}
